import {
  useContext,
  useRef,
  useState,
  type FormEventHandler,
  type ReactNode,
} from "react";
import { useForm, type UseFormRegisterReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  AuthContext,
  type AuthContextValue,
  type AuthUser,
} from "../hooks/useAuth";
import { getAuthErrorMessage } from "../auth-errors";

const schema = z.object({
  name: z.string().trim().min(1, "Enter your account name."),
  password: z.string().min(1, "Enter your account password."),
});

export interface AccountNameFormRenderModel {
  email: string;
  pending: boolean;
  error: string | null;
  saved: boolean;
  onSubmit: FormEventHandler<HTMLFormElement>;
  name: { registration: UseFormRegisterReturn<"name">; error?: string };
  password: { registration: UseFormRegisterReturn<"password">; error?: string };
}
interface AccountNameFormProps {
  render: (model: AccountNameFormRenderModel) => ReactNode;
}

/** Personal Firebase label only. Apps own native presentation, not the write state machine. */
export function AccountNameForm({ render }: AccountNameFormProps) {
  const auth = useContext(AuthContext);
  const user = auth?.user;
  if (!auth || !user?.email || user.uid.startsWith("work:")) return null;
  return (
    <PersonalAccountNameForm
      key={user.uid}
      user={user}
      auth={auth}
      render={render}
    />
  );
}

function PersonalAccountNameForm({
  user,
  auth,
  render,
}: AccountNameFormProps & {
  user: AuthUser;
  auth: AuthContextValue;
}) {
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const lock = useRef(false);
  const completed = useRef(false);
  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: { name: user.displayName ?? "", password: "" },
  });
  const submit = form.handleSubmit(async (values) => {
    if (lock.current || completed.current) return;
    lock.current = true;
    setError(null);
    setSaved(false);
    try {
      if (!auth.updateAccountName)
        throw new Error("Account-name service unavailable");
      await auth.updateAccountName(values.name, values.password);
      // Also fence a submission whose async validation began before success.
      completed.current = true;
      setSaved(true);
    } catch (failure) {
      setError(getAuthErrorMessage(failure));
    } finally {
      form.resetField("password");
      lock.current = false;
    }
  });
  return render({
    email: user.email!,
    pending: form.formState.isSubmitting,
    error,
    saved,
    onSubmit: submit,
    name: {
      registration: form.register("name"),
      error: form.formState.errors.name?.message,
    },
    password: {
      registration: form.register("password"),
      error: form.formState.errors.password?.message,
    },
  });
}
